import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Hand } from 'lucide-react';
import { InstantFeedback } from './InstantFeedback';
import { mediaUrl } from '@/lib/mediaUrl';

const ItemImage = ({ item }) => (item.imageUrl ? <img src={mediaUrl(item.imageUrl)} alt={item.imageAlt || ''} className="h-12 w-12 object-cover rounded-md shrink-0" /> : null);

// Tap-to-select / tap-to-place board (with desktop drag) and instant per-drop feedback.
export const CategoryBoard = ({ items, zones, savedPlaced, isCompleted, onSaveData, onComplete, testPrefix = 'board' }) => {
  const [placed, setPlaced] = useState(() => {
    if (isCompleted) return Object.fromEntries(items.map(i => [i.id, i.zoneId]));
    return Object.fromEntries(Object.entries(savedPlaced || {}).filter(([id, z]) => items.find(i => i.id === id)?.zoneId === z));
  });
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [flash, setFlash] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const pool = items.filter(i => !placed[i.id]);
  const done = items.length > 0 && pool.length === 0;

  const place = (itemId, zoneId) => {
    const item = items.find(i => i.id === itemId);
    if (!item || placed[itemId]) return;
    const ok = item.zoneId === zoneId;
    setSelected(null);
    setFeedback({ ok, key: Date.now() });
    setFlash({ zoneId, ok });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setFlash(null), 1200);
    if (!ok) return;
    const next = { ...placed, [itemId]: zoneId };
    setPlaced(next);
    const allDone = items.every(i => next[i.id]);
    onSaveData({ placed: next, correct: allDone });
    if (allDone && !isCompleted) { onComplete(); toast.success('Activity complete!'); }
  };

  return (
    <div className="space-y-4" data-testid={`${testPrefix}-board`}>
      <InstantFeedback feedback={feedback} testId={`${testPrefix}-feedback`} />

      {!done && (
        <div>
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
            <Hand className="h-3.5 w-3.5" /> Tap an item, then tap the category it belongs to (or drag it on desktop).
          </p>
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 min-h-[60px]" data-testid={`${testPrefix}-pool`}>
            {pool.map(item => (
              <button
                key={item.id}
                type="button"
                draggable
                onDragStart={(e) => { e.dataTransfer.setData('text/plain', item.id); setSelected(item.id); }}
                onClick={() => setSelected(selected === item.id ? null : item.id)}
                className={`min-h-[44px] px-4 py-2 rounded-lg border-2 text-sm text-left transition-[transform,background-color,border-color] duration-150 cursor-grab active:cursor-grabbing ${
                  selected === item.id ? 'border-[#20acf6] bg-blue-50 scale-[1.02] shadow-sm' : 'border-slate-200 bg-white hover:border-[#20acf6]/60'
                }`}
                data-testid={`${testPrefix}-item-${item.id}`}
                aria-pressed={selected === item.id}
              >
                <span className="flex items-center gap-2"><ItemImage item={item} />{item.text}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={`grid gap-3 ${zones.length > 2 ? 'sm:grid-cols-2' : zones.length === 2 ? 'sm:grid-cols-2' : ''}`}>
        {zones.map(zone => {
          const zoneItems = items.filter(i => placed[i.id] === zone.id);
          const f = flash?.zoneId === zone.id ? flash : null;
          return (
            <div
              key={zone.id}
              role="button"
              tabIndex={0}
              onClick={() => selected && place(selected, zone.id)}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && selected) place(selected, zone.id); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); place(e.dataTransfer.getData('text/plain'), zone.id); }}
              className={`min-h-[96px] p-3 rounded-xl border-2 transition-colors duration-200 ${
                f ? (f.ok ? 'border-green-500 bg-green-50' : 'border-red-400 bg-red-50 animate-[shake_0.3s_ease-in-out]') :
                selected ? 'border-[#20acf6] border-dashed bg-[#20acf6]/5 cursor-pointer' : 'border-slate-200 bg-white'
              }`}
              data-testid={`${testPrefix}-zone-${zone.id}`}
            >
              <p className="font-semibold text-sm text-[#2a3e5d] mb-2">{zone.label}</p>
              <div className="flex flex-wrap gap-2">
                {zoneItems.map(item => (
                  <span key={item.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-100 text-green-800 text-sm border border-green-300" data-testid={`${testPrefix}-placed-${item.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> <ItemImage item={item} /> {item.text}
                  </span>
                ))}
                {f && !f.ok && <span className="inline-flex items-center gap-1 text-xs text-red-600"><XCircle className="h-3.5 w-3.5" /> Not this one</span>}
              </div>
            </div>
          );
        })}
      </div>

      {done && (
        <div className="p-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-2" data-testid={`${testPrefix}-success`}>
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <p className="text-sm font-medium text-green-800">Well done — everything is in the right place.</p>
        </div>
      )}
    </div>
  );
};
